<template>
  <SchemaTablePage
    ref="tablePageRef"
    layout-mode="page"
    show-summary
    :search-schema="searchSchema"
    :columns="tableColumns"
    :default-query="defaultQuery"
    :request="request"
    :build-params="buildParams"
    :parse-result="parseResult"
    :table-attrs="tableAttrs"
  >
    <template #search-amountRange="{ query }">
      <div class="amount-range" style="width: 100%">
        <el-input-number
          v-model="query.minOperationAmount"
          :min="0"
          :precision="2"
          placeholder="最小金额"
          :controls="false"
        />
        <div style="width: 10px" />
        <el-input-number
          v-model="query.maxOperationAmount"
          :min="0"
          :precision="2"
          placeholder="最大金额"
          :controls="false"
        />
      </div>
    </template>

    <template #colMemberAccount="{ row }">
      <MemberAccountCell :row="row" :username="row.memberAccount" :uid="row.uid" :vip-level="row.memberLevel" />
    </template>
    <template #colOperationAmount="{ row }">{{ row.operationAmount | centsToYuan }}</template>
  </SchemaTablePage>
</template>

<script>
import { getModifyPlayerGoldList } from '@/api/finance/moneyManagement'
import { centsToYuan, yuanToCents } from '@/utils/amount'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'

const ACTION_MAP = {
  1: '会员上分',
  2: '会员下分',
  3: '代理上分',
  4: '代理下分',
}
const TYPE_OPTIONS = [
  { label: '全部', value: '' },
  { label: '会员上分', value: '会员上分' },
  { label: '会员下分', value: '会员下分' },
  { label: '代理上分', value: '代理上分' },
  { label: '代理下分', value: '代理下分' },
]

export default {
  name: 'ManageManualScoreHistoryTab',
  components: { MemberAccountCell, SchemaTablePage },
  filters: {
    centsToYuan,
  },
  data() {
    return {
      defaultQuery: {
        memberAccount: '',
        minOperationAmount: 0,
        maxOperationAmount: 0,
        operator: '',
        type: '',
        operationTimeRange: [],
      },
      searchSchema: [
        {
          prop: 'memberAccount',
          label: '会员账号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          key: 'amountRange',
          label: '操作金额',
          component: 'slot',
          slot: 'search-amountRange',
        },
        {
          prop: 'operator',
          label: '操作人',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'type',
          label: '类型',
          component: 'select',
          componentProps: { clearable: true },
          options: TYPE_OPTIONS,
        },
        {
          prop: 'operationTimeRange',
          label: '操作时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          // span: { xs: 24, sm: 12, md: 10 },
        },
        { component: 'actions' },
      ],
      tableColumns: [
        { prop: 'memberAccount', label: '会员账号', minWidth: 120, resizable: false, slot: 'colMemberAccount' },
        { prop: 'operationType', label: '操作类型', minWidth: 120, resizable: false },
        { prop: 'reason', label: '原因', minWidth: 150, resizable: false },
        { prop: 'operationAmount', label: '操作金额', minWidth: 120, resizable: false, slot: 'colOperationAmount' },
        { prop: 'reviewer', label: '操作人', width: 120, resizable: false },
        { prop: 'applicationTime', label: '操作时间', minWidth: 150, resizable: false },
      ],
      tableAttrs: {
        showSummary: true,
        summaryMethod: (...args) => this.getSummaries(...args),
      },
    }
  },
  methods: {
    request(params) {
      return getModifyPlayerGoldList(params)
    },
    buildParams({ query, page, limit }) {
      const params = {
        page,
        page_size: limit,
        status: 0,
      }
      if (query.memberAccount) params.username = query.memberAccount
      if (query.minOperationAmount > 0) params.amount_start = yuanToCents(query.minOperationAmount)
      if (query.maxOperationAmount > 0) params.amount_end = yuanToCents(query.maxOperationAmount)
      if (query.operator) params.operator = query.operator
      const typeMap = { 会员上分: 1, 会员下分: 2, 代理上分: 3, 代理下分: 4 }
      if (query.type && typeMap[query.type] != null) params.action = typeMap[query.type]
      if (Array.isArray(query.operationTimeRange) && query.operationTimeRange.length === 2) {
        const [start, end] = query.operationTimeRange
        const startAt = toUnixSeconds(start)
        const endAt = toUnixSeconds(end)
        if (startAt) params.start_at = startAt
        if (endAt) params.end_at = endAt
      }
      return params
    },
    parseResult(res) {
      const respData = (res && res.data) || {}
      const list = respData.list || []
      return {
        list: list.map(item => this.mapRow(item)),
        total: respData.total != null ? respData.total : 0,
      }
    },
    mapRow(item) {
      return {
        uid: item.uid,
        memberAccount: item.username || '',
        memberLevel: item.vip_level != null ? String(item.vip_level) : '',
        operationType: ACTION_MAP[item.action] != null ? ACTION_MAP[item.action] : `类型${item.action}`,
        reason: item.reason || '',
        operationAmount: item.amount,
        applicationTime: formatTimestamp(item.operator_at),
        reviewer: item.operator || '',
      }
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        if (column.property === 'operationAmount') {
          const values = data.map(item => Number(item.operationAmount) || 0)
          const totalCents = values.reduce((prev, curr) => prev + curr, 0)
          sums[index] = centsToYuan(totalCents)
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
  },
}
</script>

<style scoped>
.amount-range {
  display: flex;
  align-items: center;
  width: 100%;
}

.amount-range .el-input-number {
  flex: 1;
  min-width: 0;
}
</style>
